import { BIS_CHANNELS, BIS_BLOCK_FIELDS, BIS_FORCE_LABELS, type BisBlockData, type BisBlockKind, type BisColor, type BisFont, type BisForce, type BisRuleKind } from '@raro/domain';
import { cx, Label, Serif } from '@raro/ui';
import { useBisAssets } from './assets';

type Block = { kind?: BisBlockKind; title: string; body: string; data?: BisBlockData; force: BisForce | null; ruleKind: BisRuleKind | null };
type Pair = Record<string, string>;
const str = (d: BisBlockData, k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
const list = (d: BisBlockData, k: string) => (Array.isArray(d[k]) ? (d[k] as unknown[]).filter((x): x is string => typeof x === 'string') : []);
const rows = (d: BisBlockData, k: string) => (Array.isArray(d[k]) ? (d[k] as unknown[]).filter((x): x is Pair => Boolean(x) && typeof x === 'object') : []);
const host = (url: string) => { try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return 'página'; } };
/** `#RRGGBB` em "R G B" e se o texto por cima deve ser claro. */
function rgb(hex: string) {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return { text: `${r} ${g} ${b}`, dark: (r * 299 + g * 587 + b * 114) / 1000 < 140 };
}
/** Os arquivos de um bloco, cada um no seu quadro. `split` mostra o mesmo arquivo sobre claro e sobre escuro (logo). */
function Files({ items, split, small }: { items: Pair[]; split?: boolean; small?: boolean }) {
  const { lookup } = useBisAssets();
  if (!items.length) return null;
  return (
    <div className={cx('rr-block__files', small && 'rr-block__files--small')}>
      {items.map((x) => {
        const a = lookup(x.assetId ?? '');
        return (
          <figure key={x.assetId} className="rr-block__file">
            <div className={cx('rr-block__filebox', split && 'rr-block__filebox--split')}>
              {a?.url ? (split ? <><span><img src={a.url} alt="" loading="lazy" /></span><span><img src={a.url} alt={x.label} loading="lazy" /></span></> : <img src={a.url} alt={x.label} loading="lazy" />) : <small>arquivo</small>}
            </div>
            {!small && <figcaption>{x.label || a?.title || 'arquivo'}{a?.url && <a href={a.url} target="_blank" rel="noreferrer">abrir</a>}</figcaption>}
          </figure>
        );
      })}
    </div>
  );
}
const Part = ({ label, children }: { label: string; children: string }) => children ? <div className="rr-block__part"><Label>{label}</Label><p>{children}</p></div> : null;
const Words = ({ label, items, tone }: { label: string; items: string[]; tone?: 'off' | 'never' }) => items.length ? (
  <div className="rr-block__words"><Label>{label}</Label><div>{items.map((w) => <span key={w} className={cx('rr-block__word', tone && `rr-block__word--${tone}`)}>{w}</span>)}</div></div>
) : null;

/**
 * Um bloco do BIS como se lê: cada tipo com o desenho próprio. É a mesma leitura no livro e na tela de construir.
 * `compact` encurta para a lista de blocos (sem os exemplos longos).
 */
export function BlockView({ block, compact }: { block: Block; compact?: boolean }) {
  const kind = block.kind ?? 'note';
  const d = block.data ?? {};
  if (kind === 'statement') return (
    <div className="rr-block rr-block--statement">
      <p className="rr-block__statement">{block.body}</p>
      {!compact && <div className="rr-block__cols"><Part label="o que significa">{str(d, 'meaning')}</Part><Part label="por que importa">{str(d, 'why')}</Part></div>}
    </div>
  );
  if (kind === 'attributes') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      <div className="rr-block__attrs">{rows(d, 'items').map((x) => <div key={x.name}><b>{x.name}</b>{x.text && <p>{x.text}</p>}</div>)}</div>
    </div>
  );
  if (kind === 'audience') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      {list(d, 'needs').length > 0 && <div className="rr-block__part"><Label>o que precisa</Label><ul>{list(d, 'needs').map((x) => <li key={x}>{x}</li>)}</ul></div>}
      {!compact && <div className="rr-block__cols"><Part label="como percebe a marca">{str(d, 'perceives')}</Part><Part label="como falar com ele">{str(d, 'speakTo')}</Part></div>}
    </div>
  );
  if (kind === 'vocabulary') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      <div className="rr-block__cols rr-block__cols--3"><Words label="usa" items={list(d, 'use')} /><Words label="evita" items={list(d, 'avoid')} tone="off" /><Words label="nunca usa" items={list(d, 'never')} tone="never" /></div>
      {rows(d, 'swaps').length > 0 && <div className="rr-block__part"><Label>trocas</Label><div className="rr-block__swaps">{rows(d, 'swaps').map((x) => <p key={x.from}><s>{x.from}</s><span>→</span><b>{x.to}</b></p>)}</div></div>}
    </div>
  );
  if (kind === 'grammar') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      <div className="rr-block__table">{rows(d, 'rules').map((x) => [<span key={`${x.context}-c`} className="rr-label">{x.context}</span>, <p key={`${x.context}-r`}>{x.rule}</p>])}</div>
    </div>
  );
  if (kind === 'message') return (
    <div className="rr-block rr-block--message">
      {str(d, 'context') && <Label>{str(d, 'context')}</Label>}
      <p className="rr-block__quote"><Serif italic>“{block.body}”</Serif></p>
      {!compact && <Part label="por que funciona">{str(d, 'why')}</Part>}
    </div>
  );
  if (kind === 'palette') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      <div className="rr-block__swatches">{(rows(d, 'colors') as unknown as BisColor[]).map((c) => { const x = rgb(c.hex); return (
        <div key={c.hex + c.name} className="rr-block__swatch">
          <div style={{ background: c.hex, color: x.dark ? '#fff' : '#111' }}><b>{c.name}</b></div>
          <dl><dt>HEX</dt><dd>{c.hex}</dd><dt>RGB</dt><dd>{x.text}</dd>{c.cmyk && <><dt>CMYK</dt><dd>{c.cmyk}</dd></>}{c.pantone && <><dt>Pantone</dt><dd>{c.pantone}</dd></>}</dl>
          {c.role && <p>{c.role}</p>}
        </div>
      ); })}</div>
    </div>
  );
  if (kind === 'typography') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      <div className="rr-block__fonts">{(rows(d, 'fonts') as unknown as BisFont[]).map((f) => (
        <div key={f.family}><span style={{ fontFamily: `"${f.family}", var(--rr-font-sans, sans-serif)` }}>{f.family}</span><p>{[f.role, f.weights && `pesos: ${f.weights}`, f.fallback && `substituta: ${f.fallback}`].filter(Boolean).join(' · ')}</p></div>
      ))}</div>
    </div>
  );
  if (kind === 'logo') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      <Files items={rows(d, 'versions')} split />
      {!compact && <div className="rr-block__cols"><Part label="área de segurança">{str(d, 'clearSpace')}</Part><Part label="tamanho mínimo">{str(d, 'minSize')}</Part></div>}
      {!compact && list(d, 'dont').length > 0 && <div className="rr-block__part rr-block__part--dont"><Label>usos errados</Label><ul>{list(d, 'dont').map((x) => <li key={x}>{x}</li>)}</ul></div>}
    </div>
  );
  if (kind === 'seal') {
    const to = str(d, 'endsOn'), from = str(d, 'startsOn');
    const day = (v: string) => v.split('-').reverse().join('/');
    const over = Boolean(to) && to < new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);
    return (
      <div className="rr-block">
        <p className="rr-block__lead">{block.body}</p>
        <Files items={rows(d, 'files')} split />
        <Part label="vigência">{over ? `encerrado em ${day(to)}: saiu das peças` : [from && `de ${day(from)}`, to ? `até ${day(to)}` : 'sem data para acabar'].filter(Boolean).join(' ')}</Part>
        {!compact && list(d, 'dont').length > 0 && <div className="rr-block__part rr-block__part--dont"><Label>usos errados</Label><ul>{list(d, 'dont').map((x) => <li key={x}>{x}</li>)}</ul></div>}
      </div>
    );
  }
  if (kind === 'channel') return (
    <div className="rr-block">
      {str(d, 'channel') && <Label>{BIS_CHANNELS.find((c) => c.key === str(d, 'channel'))?.label ?? str(d, 'channel')}</Label>}
      <p className="rr-block__lead">{block.body}</p>
      {!compact && <div className="rr-block__cols"><Part label="para que serve">{str(d, 'purpose')}</Part><Part label="como a marca fala aqui">{str(d, 'tone')}</Part></div>}
      {!compact && (str(d, 'length') || list(d, 'hashtags').length > 0) && <div className="rr-block__cols"><Part label="tamanho do texto">{str(d, 'length')}</Part><Part label="hashtags">{list(d, 'hashtags').join(' ')}</Part></div>}
      {!compact && (list(d, 'do').length > 0 || list(d, 'dont').length > 0) && (
        <div className="rr-block__cols">
          {list(d, 'do').length > 0 && <div className="rr-block__part"><Label>faça</Label><ul>{list(d, 'do').map((x) => <li key={x}>{x}</li>)}</ul></div>}
          {list(d, 'dont').length > 0 && <div className="rr-block__part rr-block__part--dont"><Label>não faça</Label><ul>{list(d, 'dont').map((x) => <li key={x}>{x}</li>)}</ul></div>}
        </div>
      )}
    </div>
  );
  if (kind === 'character') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      <Files items={rows(d, 'poses')} />
      {!compact && <div className="rr-block__cols"><Part label="como é">{str(d, 'look')}</Part><Part label="jeito">{str(d, 'personality')}</Part></div>}
      {!compact && (list(d, 'do').length > 0 || list(d, 'dont').length > 0) && (
        <div className="rr-block__cols">
          {list(d, 'do').length > 0 && <div className="rr-block__part"><Label>aparece assim</Label><ul>{list(d, 'do').map((x) => <li key={x}>{x}</li>)}</ul></div>}
          {list(d, 'dont').length > 0 && <div className="rr-block__part rr-block__part--dont"><Label>nunca</Label><ul>{list(d, 'dont').map((x) => <li key={x}>{x}</li>)}</ul></div>}
        </div>
      )}
    </div>
  );
  if (kind === 'program') {
    const partners = rows(d, 'partners'), colors = rows(d, 'colors');
    const from = str(d, 'startsOn'), to = str(d, 'endsOn');
    const day = (v: string) => v.split('-').reverse().join('/');
    const over = Boolean(to) && to < new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);
    return (
      <div className="rr-block">
        <p className="rr-block__lead">{block.body}</p>
        <Files items={rows(d, 'logos')} split />
        {str(d, 'tagline') && <p className="rr-block__quote">{str(d, 'tagline')}</p>}
        <div className="rr-block__cols">
          <Part label="vigência">{over ? `encerrado em ${day(to)}: saiu das opções do MODO` : [from && `de ${day(from)}`, to ? `até ${day(to)}` : 'sem data para acabar'].filter(Boolean).join(' ')}</Part>
          {!compact && list(d, 'aliases').length > 0 && <Part label="também chamado de">{list(d, 'aliases').join(' · ')}</Part>}
        </div>
        {!compact && colors.length > 0 && <div className="rr-block__part"><Label>cores próprias</Label><p>{colors.map((x) => `${x.name} ${x.hex}`).join(' · ')}</p></div>}
        {!compact && partners.length > 0 && <div className="rr-block__part"><Label>parceiros, na ordem da régua</Label><ul>{partners.map((x) => <li key={`${x.role}${x.name}`}>{x.role ? `${x.role}: ` : ''}{x.name}</li>)}</ul></div>}
        {!compact && rows(d, 'partnerLogos').length > 0 && <Files items={rows(d, 'partnerLogos')} small />}
      </div>
    );
  }
  if (kind === 'photography' || kind === 'piece') {
    const photo = kind === 'photography';
    const parts: [string, string][] = photo
      ? [['o que faz a foto ser da marca', 'style'], ['luz, cor e tratamento', 'light'], ['enquadramento e lente', 'framing'], ['quem aparece e como', 'people'], ['lugares, objetos e produto', 'settings']]
      : [['formato e medidas', 'format'], ['composição', 'composition'], ['hierarquia do texto', 'hierarchy'], ['a imagem na peça', 'imagery'], ['como o texto aparece', 'typeUse']];
    return (
      <div className="rr-block">
        <p className="rr-block__lead">{block.body}</p>
        <Files items={rows(d, photo ? 'reference' : 'examples')} />
        {!compact && <div className="rr-block__cols">{parts.map(([label, key]) => <Part key={key} label={label}>{str(d, key)}</Part>)}</div>}
        {!compact && photo && rows(d, 'formats').length > 0 && <div className="rr-block__part"><Label>formatos</Label><ul>{rows(d, 'formats').map((x) => <li key={x.name}>{x.name}{x.how ? `: ${x.how}` : ''}</li>)}</ul></div>}
        {!compact && (list(d, 'do').length > 0 || list(d, 'dont').length > 0) && (
          <div className="rr-block__cols">
            {list(d, 'do').length > 0 && <div className="rr-block__part"><Label>faça</Label><ul>{list(d, 'do').map((x) => <li key={x}>{x}</li>)}</ul></div>}
            {list(d, 'dont').length > 0 && <div className="rr-block__part rr-block__part--dont"><Label>não faça</Label><ul>{list(d, 'dont').map((x) => <li key={x}>{x}</li>)}</ul></div>}
          </div>
        )}
      </div>
    );
  }
  if (kind === 'icons' || kind === 'pattern' || kind === 'illustration') return (
    <div className="rr-block">
      <p className="rr-block__lead">{block.body}</p>
      <Files items={rows(d, 'items')} small={kind === 'icons'} />
      {!compact && <Part label={kind === 'icons' ? 'o traço' : 'onde entram'}>{str(d, kind === 'icons' ? 'style' : 'usage')}</Part>}
      {!compact && kind === 'illustration' && (list(d, 'do').length > 0 || list(d, 'dont').length > 0) && (
        <div className="rr-block__cols">
          {list(d, 'do').length > 0 && <div className="rr-block__part"><Label>faça</Label><ul>{list(d, 'do').map((x) => <li key={x}>{x}</li>)}</ul></div>}
          {list(d, 'dont').length > 0 && <div className="rr-block__part rr-block__part--dont"><Label>não faça</Label><ul>{list(d, 'dont').map((x) => <li key={x}>{x}</li>)}</ul></div>}
        </div>
      )}
    </div>
  );
  if (kind === 'rule') {
    const compliance = block.ruleKind === 'compliance';
    return (
      <div className="rr-block rr-block--rule">
        <div className="rr-block__rulehead">
          {block.force && <span className={cx('rr-bis__force', `rr-bis__force--${block.force}`)}>{BIS_FORCE_LABELS[block.force]}</span>}
          {compliance && <span className="rr-block__norm">{[str(d, 'issuer'), str(d, 'norm')].filter(Boolean).join(' · ') || 'conformidade'}{str(d, 'sourceUrl') && <> · <a href={str(d, 'sourceUrl')} target="_blank" rel="noreferrer">{host(str(d, 'sourceUrl'))}</a></>}</span>}
        </div>
        <p className="rr-block__rule">{block.body}</p>
        {!compact && <>
          <Part label="como conferir">{str(d, 'check')}</Part>
          {(list(d, 'do').length > 0 || list(d, 'dont').length > 0) && (
            <div className="rr-block__cols">
              {list(d, 'do').length > 0 && <div className="rr-block__part"><Label>faça</Label><ul>{list(d, 'do').map((x) => <li key={x}>{x}</li>)}</ul></div>}
              {list(d, 'dont').length > 0 && <div className="rr-block__part rr-block__part--dont"><Label>não faça</Label><ul>{list(d, 'dont').map((x) => <li key={x}>{x}</li>)}</ul></div>}
            </div>
          )}
          {compliance && <div className="rr-block__cols"><Part label="o que aciona">{str(d, 'trigger')}</Part><Part label="quem libera">{str(d, 'approver')}</Part></div>}
          {compliance && <Part label="texto obrigatório">{str(d, 'mandatoryText')}</Part>}
        </>}
      </div>
    );
  }
  return <div className="rr-block"><p className="rr-block__lead">{block.body}</p></div>;
}

/** Quantos campos do tipo o bloco ainda não tem: é o que a tela de construir mostra como "incompleto". */
export function missingFields(kind: BisBlockKind, data: BisBlockData, ruleKind: BisRuleKind | null): string[] {
  return BIS_BLOCK_FIELDS[kind].filter((f) => (!f.only || f.only === ruleKind) && !['approver', 'mandatoryText', 'dont', 'do', 'speakTo', 'never', 'avoid', 'swaps', 'usage', 'style'].includes(f.key)).filter((f) => { const v = data[f.key]; return !v || (Array.isArray(v) && !v.length); }).map((f) => f.label);
}
